import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PersonIcon from "@mui/icons-material/Person";
import AssessmentIcon from "@mui/icons-material/Assessment";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import FormDate from "../../components/formFeild/FormDate";
import baseUrl from "../../api/baseUrl";
import getDecryptedToken from "../../api/getDecryptedToken";

interface FilterForm {
  startDate: string;
  endDate: string;
}

interface CommissionLineItem {
  treatment: string;
  isOthers?: boolean;
  price: number;
  quantity: number;
  commission: number;
  paymentDate?: string;
  amountReceived?: number;
}

interface DentistCommission {
  dentistId: string;
  dentistName: string;
  totalCommission: number;
  treatmentCount: number;
  lineItems: CommissionLineItem[];
}

function getDefaultMonthRange(): { startDate: string; endDate: string } {
  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - 1);
  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  };
}

function formatRs(value: unknown): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return "Rs 0.00";
  return `Rs ${n.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatPaymentDate(value?: string): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toISOString().split("T")[0];
}

function MyPayment() {
  const dentistId = getDecryptedToken("eid") || "";
  const userName = getDecryptedToken("userName") || "My account";
  const defaultRange = getDefaultMonthRange();

  const methods = useForm<FilterForm>({
    defaultValues: defaultRange,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<DentistCommission | null>(null);

  const fetchReport = useCallback(
    async (formData: FilterForm) => {
      if (!formData.startDate || !formData.endDate) return;
      if (!dentistId) {
        setError("Could not identify your account. Please sign in again.");
        setReport(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await baseUrl.get("payment/commissionReport", {
          params: {
            startDate: formData.startDate,
            endDate: formData.endDate,
            dentistId,
          },
        });

        const rows: DentistCommission[] = response.data?.data ?? [];
        const mine =
          rows.find((row) => String(row.dentistId) === String(dentistId)) ??
          rows[0] ??
          null;

        setReport(
          mine
            ? {
                ...mine,
                lineItems: [...(mine.lineItems ?? [])].sort((a, b) =>
                  String(b.paymentDate ?? "").localeCompare(String(a.paymentDate ?? ""))
                ),
              }
            : {
                dentistId,
                dentistName: userName,
                totalCommission: 0,
                treatmentCount: 0,
                lineItems: [],
              }
        );
      } catch (err: any) {
        console.error(err);
        setReport(null);
        setError(err.response?.data?.message || "Failed to load payment report");
      } finally {
        setLoading(false);
      }
    },
    [dentistId, userName]
  );

  useEffect(() => {
    void fetchReport(defaultRange);
  }, [fetchReport]);

  const lineItems = report?.lineItems ?? [];
  const hasResults = lineItems.length > 0;

  const summary = useMemo(
    () => ({
      totalCommission: report?.totalCommission ?? 0,
      treatmentCount: report?.treatmentCount ?? 0,
      paymentCount: lineItems.length,
    }),
    [report, lineItems.length]
  );

  return (
    <div className="space-y-3">
      <div className="bg-white dark:bg-slate-800 rounded-xl md:rounded-2xl shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 overflow-hidden">
        <div className="px-4 md:px-5 py-4 bg-gradient-to-r from-primary to-primaryLight border-b border-primary/20 flex items-center gap-3">
          <ReceiptIcon className="text-white" />
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-white">My Payment</h2>
            <p className="text-sm text-white/80 truncate flex items-center gap-1.5">
              <PersonIcon sx={{ fontSize: 16 }} />
              {userName}
            </p>
          </div>
        </div>

        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit((values) => fetchReport(values))}
            className="p-4 border-b border-slate-100 dark:border-slate-700 flex flex-wrap items-end gap-3"
          >
            <FormDate label="From" name="startDate" sameSize />
            <FormDate label="To" name="endDate" sameSize />
            <button
              type="submit"
              disabled={loading}
              className="h-8 px-4 rounded-lg bg-gradient-to-r from-primary to-primaryLight text-white font-medium hover:shadow-lg hover:shadow-primary/20 transition-all text-sm disabled:opacity-50 flex items-center gap-2"
            >
              <AssessmentIcon sx={{ fontSize: 18 }} />
              {loading ? "Loading…" : "View report"}
            </button>
          </form>
        </FormProvider>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 p-4 border-b border-slate-100 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/20">
          <div className="rounded-xl border border-slate-200/80 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 py-3">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Total commission
            </p>
            <p className="mt-1 text-xl font-bold text-emerald-600 dark:text-emerald-400 tabular-nums">
              {formatRs(summary.totalCommission)}
            </p>
          </div>
          <div className="rounded-xl border border-slate-200/80 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 py-3">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Treatments
            </p>
            <p className="mt-1 text-xl font-bold text-slate-800 dark:text-slate-100 tabular-nums">
              {summary.treatmentCount}
            </p>
          </div>
          <div className="rounded-xl border border-slate-200/80 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 py-3">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Payment lines
            </p>
            <p className="mt-1 text-xl font-bold text-slate-800 dark:text-slate-100 tabular-nums">
              {summary.paymentCount}
            </p>
          </div>
        </div>

        {error ? (
          <div className="p-6 text-center text-sm text-red-600 dark:text-red-400">{error}</div>
        ) : loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : !hasResults ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
            <SearchOffIcon className="text-slate-400 dark:text-slate-500 mb-3" sx={{ fontSize: 40 }} />
            <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 mb-1">
              No commission records
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm">
              No paid treatments with commission were found for the selected date range.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-600">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Treatment
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Received
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                    Commission
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {lineItems.map((item, index) => (
                  <tr
                    key={`${item.treatment}-${item.paymentDate}-${index}`}
                    className="hover:bg-slate-50/70 dark:hover:bg-slate-700/40 transition-colors"
                  >
                    <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300 whitespace-nowrap">
                      {formatPaymentDate(item.paymentDate)}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-800 dark:text-slate-100">
                      {item.isOthers ? (
                        <>
                          Others{item.treatment ? ` – ${item.treatment}` : ""}
                        </>
                      ) : (
                        item.treatment || "—"
                      )}
                      {item.price != null ? (
                        <span className="block text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          {formatRs(item.price)} × {item.quantity || 1}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-slate-600 dark:text-slate-300 tabular-nums">
                      {item.amountReceived != null ? formatRs(item.amountReceived) : "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-semibold text-emerald-600 dark:text-emerald-400 tabular-nums">
                      {formatRs(item.commission)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default MyPayment;
